import { Field, Formik, useFormikContext } from 'formik';
import { ReactNode } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { CollectionPointSearchParams } from '../../../../../types/collectionPoints';
import { joinZipCodeCity } from '../../../../../utils/address';
import { config } from '../../../../../utils/config';
import getValidator from '../../../../../utils/getValidator';
import Button from '../../../../atoms/Button';
import CityField from '../../../../fields/CityField';
import useAddressCompletion from '../../../../fields/ZipCodeCityField/useAddressCompletion';
import ZipCodeField from '../../../../fields/ZipCodeField';
import SelectField from '../../../../formik/SelectField';
import TextField from '../../../../formik/TextField';
import './style.scss';

const SearchForm = ({ isLoading }: { isLoading?: boolean }) => {
  const { formatMessage } = useIntl();
  const { submitForm } = useFormikContext();
  const country = config.country;
  const addressFormConfig = config.addressForms[country];
  const { zipCode, city } = addressFormConfig.zipCodeCity;
  const { onCityBlur, onZipCodeBlur, suggestedZipCodes } = useAddressCompletion({ country });
  return (
    <div className="form">
      <div className="form__row">
        <div className="form__field-container" style={{ flex: 2 }}>
          <Field
            name="streetHouseNumber"
            component={TextField}
            label={formatMessage({ id: 'address.streetHouseNumber' })}
            maxLength={60}
            data-test-id="shipping-collection-point-address"
            validate={getValidator({ required: true })}
          />
        </div>
        <ZipCodeField
          testIdPrefix="shipping-collection-point"
          country={country}
          onBlur={onZipCodeBlur}
          suggestedZipCodes={suggestedZipCodes}
          {...zipCode}
        />
      </div>
      <div className="form__row">
        <CityField testIdPrefix="shipping-collection-point" onBlur={onCityBlur} {...city} />
        <div className="form__field-container search__maximum-distance">
          <Field
            name="maximumDistance"
            component={SelectField}
            label={formatMessage({ id: 'shipping.collectionPoint.search.maximumDistance' })}
            options={config.collectionPoints.maximumDistanceOptions.map(distanceInKm => ({
              value: distanceInKm * 1000,
              label: formatMessage({ id: 'shipping.collectionPoint.location.distance' }, { distanceInKm }),
            }))}
            validate={getValidator({ required: true })}
          />
        </div>
        <div className="form__field-container hide-mobile">
          <Button
            type="button"
            secondary
            onClick={() => submitForm()}
            disabled={isLoading}
            isLoading={isLoading}
            data-test-id="shipping-collection-point-search"
          >
            <FormattedMessage id="generic.search" />
          </Button>
        </div>
      </div>
      <div className="form__field-container show-mobile">
        <Button
          type="button"
          secondary
          onClick={() => submitForm()}
          disabled={isLoading}
          isLoading={isLoading}
          data-test-id="shipping-collection-point-search"
        >
          <FormattedMessage id="generic.search" />
        </Button>
      </div>
    </div>
  );
};

type SearchSectionProps = {
  onSearch: (searchParams: CollectionPointSearchParams) => Promise<void>;
  onSearchEdit: () => void;
  isEditing: boolean;
  initialValues: CollectionPointSearchParams;
  isLoading?: boolean;
  filters: ReactNode;
  dropdown: ReactNode;
};

const SearchSection = ({
  onSearch,
  onSearchEdit,
  isEditing,
  initialValues,
  isLoading,
  filters,
  dropdown,
}: SearchSectionProps) => (
  <Formik initialValues={initialValues} onSubmit={onSearch} enableReinitialize>
    {({ values }) => (
      <>
        <div className="collection-point-search-section">
          {!isEditing ? (
            <div className="collection-point-search-section__stored">
              <p className="collection-point-search-section__stored-details">
                {initialValues.streetHouseNumber} &bull;{' '}
                {joinZipCodeCity({
                  country: { iso2Code: config.country },
                  zipCode: initialValues.zipCode,
                  city: initialValues.city,
                })}
              </p>
              <Button
                type="button"
                className="collection-point-search-section__edit-button"
                onClick={onSearchEdit}
                data-test-id="shipping-pickup-search-edit"
              >
                <FormattedMessage id="generic.edit" />
              </Button>
            </div>
          ) : (
            <SearchForm isLoading={isLoading} />
          )}
        </div>
        {filters}
        {isEditing && !values.streetHouseNumber && dropdown}
      </>
    )}
  </Formik>
);

export default SearchSection;
